import {
  convertBase64ToBytes,
  convertBase64URLToBytes,
  convertBytesToBase64,
  convertBytesToBase64URL,
  convertBytesToHex,
  convertBytestoString,
  convertHexToBytes,
  convertStringToBytes,
} from '../utils/string.js';
import {
  GamoraFoundationGuard,
  GamoraFoundationGuardConstructorType,
} from '../foundation/guard/guard.js';
import { assert } from '../utils/assert.js';
import { GamoraErrorValidation } from '../errors/validation.error.js';

/**
 * Input type
 */
export type GamoraBase64URLGuardInput = string;

/**
 * GamoraBase64URLGuard
 * A typesafe and guarded Base64URL encoded type
 * See (RFC 4648).
 *
 * @example "U3Vic3QuaWQgcm9ja3M="
 * @public
 */
export class GamoraBase64URLGuard extends GamoraFoundationGuard<GamoraBase64URLGuardInput> {
  protected _base64url = '';

  /**
   * Valid Base64URL Regex
   */
  static RegExp = /^[A-Za-z0-9_-]*$/;

  /**
   * Constructor
   * @param input The input
   */
  constructor(input: string) {
    super();
    this.base64url = input;
  }

  /**
   * Set Base64URL
   * @param base64url The base64url
   */
  set base64url(base64url: string) {
    assert(
      GamoraBase64URLGuard.RegExp.test(base64url),
      'Invalid base64url argument: invalid GamoraBase64URLGuard.',
      GamoraErrorValidation,
    );
    this._base64url = base64url;
  }

  /**
   * Get GamoraBase64URLGuard
   */
  get base64url() {
    return this._base64url;
  }

  /**
   * Gets GamoraBase64URLGuard Length
   */
  get length() {
    return this._base64url.length;
  }

  /**
   * Gets from JSON
   * @param input Input string
   */
  static override fromJSON<TClass extends GamoraBase64URLGuard>(
    this: GamoraFoundationGuardConstructorType<
      TClass,
      GamoraBase64URLGuardInput
    >,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    return new this(input);
  }

  /**
   * Gets from Base64URL
   */
  static override fromBase64URL<TClass extends GamoraBase64URLGuard>(
    this: GamoraFoundationGuardConstructorType<
      TClass,
      GamoraBase64URLGuardInput
    >,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    return new this(input);
  }

  /**
   * Gets from empty
   */
  static override Empty<TClass extends GamoraBase64URLGuard>(
    this: GamoraFoundationGuardConstructorType<
      TClass,
      GamoraBase64URLGuardInput
    >,
  ) {
    return new this('');
  }

  /**
   * Tests if input string is valid GamoraBase64URLGuard
   * @param input The input string
   */
  static Test<TClass extends GamoraBase64URLGuard>(
    this: GamoraFoundationGuardConstructorType<
      TClass,
      GamoraBase64URLGuardInput
    >,
    input: string,
  ): boolean {
    return GamoraBase64URLGuard.RegExp.test(input);
  }

  /**
   * Gets from Base64
   */
  static override fromBase64<TClass extends GamoraBase64URLGuard>(
    this: GamoraFoundationGuardConstructorType<
      TClass,
      GamoraBase64URLGuardInput
    >,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    const bytes = convertBase64ToBytes(input);
    return GamoraBase64URLGuard.fromJSON(convertBytesToBase64URL(bytes));
  }

  /**
   * Gets from Base64
   */
  static override fromHex<TClass extends GamoraBase64URLGuard>(
    this: GamoraFoundationGuardConstructorType<
      TClass,
      GamoraBase64URLGuardInput
    >,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    const bytes = convertHexToBytes(input);
    return GamoraBase64URLGuard.fromJSON(convertBytesToBase64URL(bytes));
  }

  /**
   * Gets from UTF8
   */
  static override fromUTF8<TClass extends GamoraBase64URLGuard>(
    this: GamoraFoundationGuardConstructorType<
      TClass,
      GamoraBase64URLGuardInput
    >,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    const bytes = convertStringToBytes(input);
    return GamoraBase64URLGuard.fromJSON(convertBytesToBase64URL(bytes));
  }

  /**
   * Gets from Uint8Array
   */
  static fromUint8Array<TClass extends GamoraBase64URLGuard>(
    this: GamoraFoundationGuardConstructorType<
      TClass,
      GamoraBase64URLGuardInput
    >,
    input: unknown,
  ) {
    assert(
      input instanceof Uint8Array,
      'Invalid input argument: not a valid Uint8Array.',
      GamoraErrorValidation,
    );
    return GamoraBase64URLGuard.fromJSON(convertBytesToBase64URL(input));
  }

  /**
   * Gets UTF8
   */
  override toUTF8() {
    const bytes = convertBase64URLToBytes(this.base64url);
    return convertBytestoString(bytes);
  }

  /**
   * Gets base64
   */
  override toBase64() {
    const bytes = convertBase64URLToBytes(this.base64url);
    return convertBytesToBase64(bytes);
  }

  /**
   * Gets hex
   */
  override toHex(): string {
    const bytes = convertBase64URLToBytes(this.base64url);
    return convertBytesToHex(bytes);
  }

  /**
   * Checks if base64url is empty
   */
  isEmpty() {
    return this.base64url === '';
  }

  /**
   * Gets JSON
   */
  override toJSON() {
    return this.base64url;
  }

  /**
   * Gets base64url
   */
  override toBase64URL() {
    return this.base64url;
  }

  /**
   * Gets base64url
   */
  override toString() {
    return this.base64url;
  }

  /**
   * Clone
   */
  override clone() {
    return new (this.constructor as GamoraFoundationGuardConstructorType<
      this,
      GamoraBase64URLGuardInput
    >)(this.toJSON());
  }
}
